<template>
	<div>
		<div
			v-if="isFixed"
			:style="{ height: `${placeholderHeight}px` }"
			class="header-placeholder"
		></div>

		<header
			ref="header"
			id="header"
			class="header container"
			:class="{
				'header--fixed': isFixed,
				'header--sidebar-open': isSidebarVisible,
				'header--with-back-icon': isMobileInfoVisible
			}"
		>
			<button
				v-if="isMobileInfoVisible"
				class="back-button"
				@click="closeMobileInfo()"
			/>
			<div class="header__top">
				<div class="d-flex justify-content-between flex-align-center">
					<h1
						class="header__top__venue-name prevent-highlight-mobile"
						@click="goToTop()"
					>
						{{ venueInfo.name }}
					</h1>
					<domains-switcher
						class="switcher static mr-0 mt-auto mb-0 domains-switcher--pink"
						title="Event calendar"
						sub-title="Tickets"
					/>
					<div
						v-if="!mobileInfoOpen || isScreenMore480"
						class="header__info"
						@click.stop="openMobileInfo"
					>
						<div class="header__info-icon">
							<icon-svg icon="ic_info" class="header__info-icon--small" />
						</div>
					</div>
				</div>
			</div>
		</header>
		<mobile-venue-info
			v-if="isMobileInfoVisible"
			:header-height="headerHeight"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import DomainsSwitcher from '@/components/DomainsSwitcher.vue'
import IconSvg from '@/components/IconSvg.vue'
import MobileVenueInfo from '@/components/VenueInfo/MobileVenueInfo.vue'

export default {
	name: 'Header',

	components: {
		MobileVenueInfo,
		IconSvg,
		DomainsSwitcher
	},

	data() {
		return {
			isFixed: false,
			headerOffsetTop: 0,
			placeholderHeight: 0,
			headerHeight: 0,
			timeout: null,
			mobileInfoOpen: false
		}
	},

	watch: {
		isScreenMore480(newVal) {
			if (newVal && this.mobileInfoOpen) {
				this.mobileInfoOpen = false
				this.unlockScroll()
			}
		}
	},

	computed: {
		...mapState('venue', ['venueInfo']),
		...mapState('ui', ['isSidebarVisible', 'isScreenMore480']),
		...mapGetters('ui', ['isScrollLocked']),

		isMobileInfoVisible() {
			return this.mobileInfoOpen && !this.isScreenMore480
		}
	},

	mounted() {
		this.$nextTick(() => {
			const header = this.$refs.header
			this.headerOffsetTop = header.offsetTop

			this.placeholderHeight = this.headerHeight = header.offsetHeight

			window.addEventListener('scroll', this.handleScroll)

			this.handleScroll()
		})
	},

	beforeDestroy() {
		window.removeEventListener('scroll', this.handleScroll)

		if (this.timeout) clearTimeout(this.timeout)
	},

	methods: {
		...mapActions('ui', ['lockScroll', 'unlockScroll']),

		handleScroll() {
			const scrollPosition = window.scrollY

			if (scrollPosition > this.headerOffsetTop && !this.isFixed) {
				if (!this.isFixed) {
					this.placeholderHeight = this.$refs.header.offsetHeight
				}
				this.isFixed = true
			} else if (scrollPosition <= this.headerOffsetTop && this.isFixed) {
				if (this.isScrollLocked) {
					return
				}
				this.isFixed = false
			}

			this.timeout = setTimeout(() => {
				this.headerHeight = this.$refs.header?.offsetHeight || 0
			}, 400)
		},

		closeMobileInfo() {
			if (this.isMobileInfoVisible) {
				this.unlockScroll()
				this.mobileInfoOpen = false
			}
		},

		openMobileInfo() {
			this.mobileInfoOpen = true
			this.lockScroll()
		},

		goToTop() {
			if (this.isMobileInfoVisible) {
				this.mobileInfoOpen = false

				return
			}

			const targetElement = document.querySelector('.main-wrap')
			const navigationBlock = document.querySelector('.header-bottom')

			if (targetElement) {
				const rect = targetElement.getBoundingClientRect()
				const scrollTop = window.scrollY
				const navigationBlockHeight = navigationBlock.getBoundingClientRect()
					.height
				const elementTop = rect.top + scrollTop - navigationBlockHeight

				window.scrollTo({
					top: elementTop,
					behavior: 'smooth'
				})
			}
		}
	}
}
</script>

<style lang="scss">
@import './src/assets/styles/variables';

.header-placeholder {
	width: 100%;

	@media (min-width: 768px) {
		display: block;
	}
}

.header {
	width: auto;
	position: relative;
	padding: 25px 16px 10px;
	background-size: cover;
	color: $white;
	background-color: $dark-purple-navy;
	transition: padding-top 0.3s ease-in-out, background 0.3s ease-in-out;
	z-index: 104;
	margin: 0 -16px;
	box-shadow: 0 10px 10px rgba(0, 0, 0, 0.5);

	@media (min-width: 768px) {
		width: 100%;
		background: $background-dark;
		padding: 20px 0 0;
		margin: 0 auto;
		box-shadow: none;
	}

	@media (min-width: 1024px) {
		padding: 36px 0 0;
	}

	&--with-back-icon {
		padding-left: 32px;

		&.header--fixed {
			padding-left: 32px;
		}

		.back-button {
			position: absolute;
			left: 8px;
			top: calc(50% + 8px);
			padding-left: 0;
			transform: translateY(-50%);

			&::before {
				left: 8px;
				height: 7px;
				width: 7px;
				border-width: 3px 3px 0 0;
			}
		}
	}

	&--fixed {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		padding: 25px 16px 10px;
		background-color: $dark-purple-navy;
		margin: 0 auto;
		z-index: 104;

		@media (min-width: 768px) {
			padding: 20px 24px 0;
			background-color: $background-dark;
		}

		@media (min-width: 1024px) {
			padding: 36px 24px 0;
		}

		.header__top__venue-name {
			@media (min-width: 768px) {
				font-size: 32px;
				line-height: 1;
			}
		}
	}

	&--sidebar-open {
		@media (max-width: 1024px) {
			z-index: 100;
		}
	}

	&__top {
		&__venue-name {
			margin: 0;
			font-size: 46px;
			line-height: 62px;
			font-weight: 600;
			letter-spacing: 0.7px;
			transition: font-size 0.3s ease, line-height 0.3s ease;

			@media (max-width: 767px) {
				font-size: 24px;
				line-height: 1.2;
				letter-spacing: 0;
			}
		}
	}

	.switcher {
		display: none;

		@media (min-width: 481px) {
			display: flex;
		}
	}

	&__info {
		@media (min-width: 481px) {
			display: none;
		}
	}

	.domains-switcher {
		max-width: 200px;
	}
}
</style>
